import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { useParams, useNavigate } from 'react-router-dom';
import { marketPulseService, type MaxPainHistoryItem } from '../../services/marketPulseService';
import GexChart from '../charts/GexChart';
import TradeEntryDialog, { type TradeLeg, type TradeEntryData } from '../shared/TradeEntryDialog';
import PositionsList from '../shared/PositionsList';
import { userService } from '../../services/userService';
import { useMarketPolling } from '../../hooks/useMarketPolling';
import type { TickerPulseSummary, TickerPulseDetail } from '../../types/marketPulse';
import { fmt } from '../../utils/formatters';

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

const REGIME_COLORS: Record<string, string> = {
  positive: '#4caf50',
  negative: '#f44336',
  neutral: '#9e9e9e',
  bullish: '#4caf50',
  bearish: '#f44336',
  elevated: '#ff9800',
  depressed: '#2196f3',
  normal: '#9e9e9e',
  unknown: '#757575',
  range_bound: '#4caf50',
  trending: '#ff9800',
  volatile: '#f44336',
  pinning_likely: '#4caf50',
  directional_risk: '#f44336',
};

const chipColor = (value: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  const map: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    positive: 'success', negative: 'error', neutral: 'default',
    bullish: 'success', bearish: 'error',
    elevated: 'warning', depressed: 'info', normal: 'default', unknown: 'default',
    range_bound: 'success', trending: 'warning', volatile: 'error',
  };
  return map[value] ?? 'default';
};

const sentimentColor = (s: string) => REGIME_COLORS[s] ?? '#9e9e9e';

const fmtPrice = (v: number): string => {
  const r = Math.round(v * 10) / 10;
  return r % 1 === 0 ? r.toFixed(0) : r.toFixed(1);
};

const strategyLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ---------------------------------------------------------------------------
// Scatter tooltip
// ---------------------------------------------------------------------------

interface ScatterPayload {
  ticker: string;
  x: number;
  y: number;
  z: number;
  sentiment: string;
}

const ScatterTooltipContent: React.FC<{ active?: boolean; payload?: Array<{ payload: ScatterPayload }> }> = ({
  active,
  payload,
}) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Paper sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}` }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{d.ticker}</Typography>
      <Typography variant="caption" display="block">GEX: {d.x.toFixed(3)}</Typography>
      <Typography variant="caption" display="block">IV Ratio: {d.y.toFixed(2)}x</Typography>
      <Typography variant="caption" display="block">OI Change: {d.z.toLocaleString()}</Typography>
      <Typography variant="caption" display="block">Retail: {d.sentiment}</Typography>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Summary view
// ---------------------------------------------------------------------------

const FILTER_OPTIONS = {
  gex_regime: ['positive', 'negative', 'neutral'],
  retail_sentiment: ['bullish', 'bearish', 'neutral'],
  iv_regime: ['elevated', 'normal', 'depressed', 'unknown'],
  overall_signal: ['range_bound', 'trending', 'volatile'],
};

// ---------------------------------------------------------------------------
// Today's Top Picks
// ---------------------------------------------------------------------------

const timeAgo = (ts: string | null | undefined) => {
  if (!ts) return '';
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const TopPicksSection: React.FC<{ onSelect: (ticker: string) => void }> = ({ onSelect }) => {
  const [picks, setPicks] = useState<import('../../services/marketPulseService').TopPick[]>([]);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    marketPulseService.getTopPicks(5)
      .then((r) => {
        setPicks(r.picks);
        // Use the most recent computed_at from any pick
        const latest = r.picks.reduce((max: string | null, p: any) => {
          if (!p.computed_at) return max;
          return !max || p.computed_at > max ? p.computed_at : max;
        }, null);
        setComputedAt(latest);
      })
      .catch(() => setPicks([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Paper sx={{ p: 2, mb: 3, textAlign: 'center' }}><CircularProgress size={24} /></Paper>;
  if (picks.length === 0) return null;

  const stratLabel = (s: { name: string; strikes: string | null } | null) => {
    if (!s) return '';
    const name = s.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return s.strikes ? `${name} · ${s.strikes}` : name;
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Today's Top Picks</Typography>
        {computedAt && (
          <Typography variant="caption" color="text.secondary">
            Updated {timeAgo(computedAt)}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {picks.map((p) => (
          <Paper
            key={p.ticker}
            variant="outlined"
            onClick={() => onSelect(p.ticker)}
            sx={{
              p: 2, flex: '1 1 280px', maxWidth: 360, cursor: 'pointer',
              borderColor: p.sentiment === 'bullish' ? 'success.main' : p.sentiment === 'bearish' ? 'error.main' : 'divider',
              borderWidth: 2,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{p.ticker}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Score</Typography>
                <Chip
                  label={`${p.score}/10`}
                  size="small"
                  color={p.score >= 7 ? 'success' : p.score >= 5 ? 'warning' : 'default'}
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>{p.thesis}</Typography>

            {p.strategy && (
              <Chip label={stratLabel(p.strategy)} size="small" variant="outlined" color="primary" sx={{ mb: 1 }} />
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Spot</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>${(p.spot_price ?? 0).toFixed(2)}</Typography>
              </Box>
              {p.entry_zone && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Entry Zone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${p.entry_zone.zone_low.toFixed(0)}-${p.entry_zone.zone_high.toFixed(0)}
                  </Typography>
                </Box>
              )}
              {p.target && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Target</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>${p.target.toFixed(0)}</Typography>
                </Box>
              )}
              {p.stop && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Stop</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>${p.stop.toFixed(0)}</Typography>
                </Box>
              )}
              {p.rr_ratio && (
                <Box>
                  <Typography variant="caption" color="text.secondary">R:R</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.rr_ratio}:1</Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
              {p.factors.slice(0, 3).map((f, i) => (
                <Chip key={i} label={f} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};

const SummaryView: React.FC<{
  data: TickerPulseSummary[];
  onSelect: (ticker: string) => void;
}> = ({ data, onSelect }) => {
  const theme = useTheme();
  const [tickerSearch, setTickerSearch] = useState('');
  const [gexFilter, setGexFilter] = useState<string>('all');
  const [retailFilter, setRetailFilter] = useState<string>('all');
  const [ivFilter, setIvFilter] = useState<string>('all');
  const [signalFilter, setSignalFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'ticker' | 'conviction_score' | 'spot_price'>('conviction_score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: typeof sortBy) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'ticker' ? 'asc' : 'desc');
    }
  };

  const filtered = data.filter((d) => {
    if (tickerSearch && !d.ticker.toLowerCase().includes(tickerSearch.toLowerCase())) return false;
    if (gexFilter !== 'all' && d.gex_regime !== gexFilter) return false;
    if (retailFilter !== 'all' && d.retail_sentiment !== retailFilter) return false;
    if (ivFilter !== 'all' && d.iv_regime !== ivFilter) return false;
    if (signalFilter !== 'all' && d.overall_signal !== signalFilter) return false;
    return true;
  }).sort((a, b) => {
    const mul = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'ticker') return mul * a.ticker.localeCompare(b.ticker);
    if (sortBy === 'conviction_score') return mul * (a.conviction_score - b.conviction_score);
    if (sortBy === 'spot_price') return mul * ((a.spot_price ?? 0) - (b.spot_price ?? 0));
    return 0;
  });

  // Scatter chart data — filter to tickers that have both gex + iv data
  const scatterData = filtered
    .filter((d) => d.gex_normalized !== undefined && d.iv_ratio != null)
    .map((d) => ({
      ticker: d.ticker,
      x: d.gex_normalized,
      y: d.iv_ratio ?? 1,
      z: Math.max(d.total_oi_change_abs, 500), // min bubble size
      sentiment: d.retail_sentiment,
    }));

  return (
    <>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search ticker..."
          value={tickerSearch}
          onChange={(e) => setTickerSearch(e.target.value)}
          sx={{ width: 140 }}
          autoComplete="off"
        />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>GEX</InputLabel>
          <Select value={gexFilter} label="GEX" onChange={(e) => setGexFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {FILTER_OPTIONS.gex_regime.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Retail</InputLabel>
          <Select value={retailFilter} label="Retail" onChange={(e) => setRetailFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {FILTER_OPTIONS.retail_sentiment.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>IV</InputLabel>
          <Select value={ivFilter} label="IV" onChange={(e) => setIvFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {FILTER_OPTIONS.iv_regime.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Signal</InputLabel>
          <Select value={signalFilter} label="Signal" onChange={(e) => setSignalFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {FILTER_OPTIONS.overall_signal.map((v) => <MenuItem key={v} value={v}>{v.replace('_', ' ')}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          {filtered.length} of {data.length} tickers
        </Typography>
      </Box>

      {/* Scatter Chart */}
      {scatterData.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Market Landscape — GEX vs IV
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            X = Net GEX (left = amplification, right = suppression) &middot; Y = IV ratio vs 12-week avg &middot; Bubble size = OI change &middot; Color = retail sentiment
          </Typography>
          <ResponsiveContainer width="100%" height={360}>
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                type="number"
                dataKey="x"
                name="GEX"
                tick={{ fontSize: 11 }}
                label={{ value: 'Net GEX Normalized', position: 'insideBottom', offset: -10, fontSize: 11 }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="IV Ratio"
                tick={{ fontSize: 11 }}
                label={{ value: 'IV Ratio', angle: -90, position: 'insideLeft', fontSize: 11 }}
              />
              <ZAxis type="number" dataKey="z" range={[40, 400]} />
              <Tooltip content={<ScatterTooltipContent />} />
              <ReferenceLine x={0} stroke={theme.palette.divider} strokeDasharray="3 3" />
              <ReferenceLine y={1} stroke={theme.palette.divider} strokeDasharray="3 3" />
              <Scatter
                data={scatterData}
                cursor="pointer"
                onClick={(entry: ScatterPayload) => { if (entry?.ticker) onSelect(entry.ticker); }}
              >
                {scatterData.map((entry, i) => (
                  <Cell key={i} fill={sentimentColor(entry.sentiment)} fillOpacity={0.75} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>Top-Right: Sell Premium</Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>Bottom-Left: Buy Premium</Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>Top-Left: Directional Risk</Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>Bottom-Right: Range Bound</Typography>
          </Box>
        </Paper>
      )}

      {/* Summary header with freshness */}
      {data.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            All Tickers ({data.length})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pulse date: {data[0]?.pulse_date} · Updated {timeAgo(data[0]?.computed_at)}
          </Typography>
        </Box>
      )}

      {/* Summary Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 60 }} sortDirection={sortBy === 'ticker' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'ticker'} direction={sortBy === 'ticker' ? sortDir : 'asc'} onClick={() => handleSort('ticker')}>
                  Ticker
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: 45 }} align="center" sortDirection={sortBy === 'conviction_score' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'conviction_score'} direction={sortBy === 'conviction_score' ? sortDir : 'desc'} onClick={() => handleSort('conviction_score')}>
                  Score
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: 75 }} align="right" sortDirection={sortBy === 'spot_price' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'spot_price'} direction={sortBy === 'spot_price' ? sortDir : 'desc'} onClick={() => handleSort('spot_price')}>
                  Spot
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: 100 }}>GEX</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 100 }}>Retail</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 105 }}>IV</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 115 }}>Signal</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>MM Summary</TableCell>
              <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' }, width: 220 }}>Opportunity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => (
              <TableRow
                key={row.ticker}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => onSelect(row.ticker)}
              >
                <TableCell sx={{ fontWeight: 600 }}>{row.ticker}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={row.conviction_score}
                    size="small"
                    color={row.conviction_score >= 7 ? 'success' : row.conviction_score >= 4 ? 'warning' : 'default'}
                    sx={{ fontWeight: 700, minWidth: 28 }}
                  />
                </TableCell>
                <TableCell align="right">{row.spot_price ? fmt.currency(row.spot_price) : '—'}</TableCell>
                <TableCell><Chip label={row.gex_regime} size="small" color={chipColor(row.gex_regime)} /></TableCell>
                <TableCell><Chip label={row.retail_sentiment} size="small" color={chipColor(row.retail_sentiment)} /></TableCell>
                <TableCell><Chip label={row.iv_regime} size="small" color={chipColor(row.iv_regime)} /></TableCell>
                <TableCell><Chip label={row.overall_signal.replace('_', ' ')} size="small" color={chipColor(row.overall_signal)} /></TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, verticalAlign: 'top' }}>
                  <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>{row.mm_summary}</Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, verticalAlign: 'top' }}>
                  <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>{row.opportunity_summary}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

// ---------------------------------------------------------------------------
// Detail view
// ---------------------------------------------------------------------------

/** Parse a strategy's strike notation (e.g. "+$250C -$260C") into TradeLeg[] */
const parseLegs = (strat: { name: string; strikes: string | null }): TradeLeg[] => {
  if (!strat.strikes) return [];
  const matches = strat.strikes.match(/([+-])\$([\d.]+)([CP])/g) || [];
  return matches.map((m) => {
    const [, side, strike, ot] = m.match(/([+-])\$([\d.]+)([CP])/)!;
    return { side: side === '+' ? 'buy' as const : 'sell' as const, option_type: ot as 'C' | 'P', strike: parseFloat(strike) };
  });
};

const DetailView: React.FC<{ data: TickerPulseDetail; onBack: () => void; refreshing?: boolean }> = ({ data, onBack, refreshing }) => {
  const theme = useTheme();
  const mm = data.mm_signals;
  const retail = data.retail;
  const opps = data.opportunities;
  const [oiRangePct, setOiRangePct] = useState(15);

  // Trade entry/close state
  const [tradeDialogOpen, setTradeDialogOpen] = useState(false);
  const [tradeStrategy, setTradeStrategy] = useState<{ name: string; strikes: string | null } | null>(null);
  const [openTrades, setOpenTrades] = useState<any[]>([]);

  useEffect(() => {
    userService.listTrades({ ticker: data.ticker, status: 'open' })
      .then(setOpenTrades)
      .catch(() => {});
  }, [data.ticker]);

  const handleEnterTrade = (strat: { name: string; strikes: string | null }) => {
    setTradeStrategy(strat);
    setTradeDialogOpen(true);
  };

  const handleTradeSubmit = async (entry: TradeEntryData) => {
    await userService.createTrade({
      ticker: entry.ticker,
      strategy_name: entry.strategyName || tradeStrategy?.name || '',
      legs: entry.legs,
      contracts: entry.contracts,
      entry_price: entry.entry_price,
      entry_date: entry.open_date,
      notes: entry.notes,
    });
    const trades = await userService.listTrades({ ticker: entry.ticker, status: 'open' });
    setOpenTrades(trades);
  };

  // Prepare OI change chart data — pivot by strike so calls and puts are side by side
  const oiByStrike = new Map<number, { strike: number; call_oi_change: number; put_oi_change: number }>();
  for (const r of mm.oi_changes) {
    const s = r.strike;
    if (!oiByStrike.has(s)) oiByStrike.set(s, { strike: s, call_oi_change: 0, put_oi_change: 0 });
    const entry = oiByStrike.get(s)!;
    if (r.option_type === 'C') entry.call_oi_change += r.oi_change;
    else entry.put_oi_change += r.oi_change;
  }
  const oiChartDataAll = Array.from(oiByStrike.values()).sort((a, b) => a.strike - b.strike);
  const oiChartData = data.spot_price
    ? oiChartDataAll.filter((d) => {
        const lo = data.spot_price * (1 - oiRangePct / 100);
        const hi = data.spot_price * (1 + oiRangePct / 100);
        return d.strike >= lo && d.strike <= hi;
      })
    : oiChartDataAll;

  // Top 10 OI movers
  const topMovers = [...mm.notable_oi_changes]
    .sort((a, b) => Math.abs(b.oi_change) - Math.abs(a.oi_change))
    .slice(0, 10);

  // High volume strikes chart
  const volChartData = retail.high_volume_strikes.map((s) => ({
    label: `$${s.strike} ${s.option_type === 'C' ? 'Call' : 'Put'}`,
    volume: s.volume,
    option_type: s.option_type,
  }));

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{data.ticker} — Market Pulse</Typography>
        <Chip label={data.overall_signal.replace('_', ' ')} size="small" color={chipColor(data.overall_signal)} />
        <Typography variant="body2" color="text.secondary">
          Spot: {fmt.currency(data.spot_price)} &middot; Expiry: {fmt.date(data.expiry)}
        </Typography>
        {data.computed_at && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {refreshing ? 'Refreshing...' : `Updated ${timeAgo(data.computed_at)}`}
          </Typography>
        )}
      </Box>

      {/* Quick Summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>Market Maker Activity</Typography>
            <Typography variant="body2">{mm.summary}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>Trading Opportunity</Typography>
            <Typography variant="body2">{opps.summary}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Trading Opportunities */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Trading Opportunities</Typography>
        <Alert severity={opps.iv_regime === 'elevated' ? 'warning' : 'info'} sx={{ mb: 2 }}>{opps.summary}</Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {opps.iv_ratio != null && (
            <Paper sx={{ px: 2, py: 1 }} variant="outlined">
              <Typography variant="caption" color="text.secondary">IV vs 12-Week Avg</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: opps.iv_regime === 'elevated' ? '#ff9800' : opps.iv_regime === 'depressed' ? '#2196f3' : 'inherit' }}>
                {opps.iv_ratio.toFixed(2)}x
              </Typography>
            </Paper>
          )}
          {opps.suggested_strategies.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>Strategies:</Typography>
              {opps.suggested_strategies.map((s, i) => {
                const strat = typeof s === 'string' ? { name: s, strikes: null } : s;
                const label = strat.strikes ? `${strategyLabel(strat.name)} · ${strat.strikes}` : strategyLabel(strat.name);
                return <Chip key={i} label={label} size="medium" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem', borderColor: 'text.disabled', color: 'text.primary', cursor: 'default' }} />;
              })}
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              const first = opps.suggested_strategies[0];
              const strat = first ? (typeof first === 'string' ? { name: first, strikes: null } : first) : { name: 'manual', strikes: null };
              handleEnterTrade(strat);
            }}
            sx={{ ml: 'auto', textTransform: 'none', px: 2, py: 0.5, fontSize: '0.75rem', minWidth: 0 }}
          >
            Trade
          </Button>
        </Box>

        {opps.key_strikes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Key Strike Levels</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {opps.key_strikes.map((ks, i) => (
                <Chip key={i} label={`$${ks.strike.toFixed(0)} — ${ks.reason}`} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {opps.entry_zone && (
          <Box sx={{ border: 1, borderColor: opps.entry_zone.direction === 'bullish' ? 'success.main' : 'error.main', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Entry Zone {opps.entry_zone.direction === 'bullish' ? '(Buy on Pullback)' : '(Sell on Rally)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={`$${opps.entry_zone.zone_low.toFixed(2)} — ${opps.entry_zone.zone_low_label}`}
                size="small"
                color={opps.entry_zone.direction === 'bullish' ? 'success' : 'error'}
                variant="outlined"
              />
              <Typography variant="body2" color="text.secondary">to</Typography>
              <Chip
                label={`$${opps.entry_zone.zone_high.toFixed(2)} — ${opps.entry_zone.zone_high_label}`}
                size="small"
                color={opps.entry_zone.direction === 'bullish' ? 'success' : 'error'}
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {opps.entry_zone.guidance}
            </Typography>
          </Box>
        )}

        {/* Open trades for this ticker */}
        {openTrades.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <PositionsList
              trades={openTrades}
              title="Your Open Positions"
              onClose={async (tradeId, closeData) => {
                await userService.closeTrade(tradeId, {
                  close_reason: closeData.close_reason,
                  close_date: closeData.close_date,
                  close_price: closeData.close_price,
                  contracts: closeData.contracts,
                });
                const trades = await userService.listTrades({ ticker: data.ticker, status: 'open' });
                setOpenTrades(trades);
              }}
              onDelete={async (tradeId) => {
                await userService.deleteTrade(tradeId);
                const trades = await userService.listTrades({ ticker: data.ticker, status: 'open' });
                setOpenTrades(trades);
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Trade entry dialog */}
      {tradeStrategy && (
        <TradeEntryDialog
          open={tradeDialogOpen}
          ticker={data.ticker}
          strategyName={strategyLabel(tradeStrategy.name)}
          legs={parseLegs(tradeStrategy)}
          expiry={data.expiry}
          onClose={() => setTradeDialogOpen(false)}
          onSubmit={handleTradeSubmit}
        />
      )}

      {/* Section 1: Market Maker Activity */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Market Maker Activity</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>{mm.summary}</Alert>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`GEX: ${mm.gex_regime}`} size="small" color={chipColor(mm.gex_regime)} />
          <Chip label={`Convergence: ${mm.convergence_signal.replace('_', ' ')}`} size="small" color={chipColor(mm.convergence_signal)} />
          {mm.gamma_flip && <Chip label={`Gamma Flip: ${fmt.currency(mm.gamma_flip)}`} size="small" variant="outlined" />}
          {mm.max_pain && <Chip label={`Max Pain: ${fmt.currency(mm.max_pain)}`} size="small" variant="outlined" />}
          {mm.top_call_wall && <Chip label={`Call Wall: ${fmt.currency(mm.top_call_wall)}`} size="small" variant="outlined" />}
          {mm.top_put_wall && <Chip label={`Put Wall: ${fmt.currency(mm.top_put_wall)}`} size="small" variant="outlined" />}
        </Box>

        {/* OI Change by Strike chart */}
        {oiChartData.length > 0 && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Open Interest Change by Strike</Typography>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={oiChartData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="strike" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(v: number) => `$${v}`} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v: number) => v.toLocaleString()} tick={{ fontSize: 11 }} />
                <Tooltip
                  labelFormatter={(label: number) => `Strike: $${label}`}
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                />
                <Legend />
                <Bar dataKey="call_oi_change" name="Call OI Change" fill="#4caf50" opacity={0.85} barSize={16} />
                <Bar dataKey="put_oi_change" name="Put OI Change" fill="#f44336" opacity={0.85} barSize={16} />
                {data.spot_price && (
                  <ReferenceLine x={data.spot_price} stroke={theme.palette.text.primary} strokeDasharray="3 3" strokeWidth={2}
                    label={{ value: `Spot $${fmtPrice(data.spot_price)}`, position: 'insideTopRight', fill: theme.palette.text.primary, fontSize: 11 }} />
                )}
                {mm.max_pain && (
                  <ReferenceLine x={mm.max_pain} stroke="#ff9800" strokeDasharray="8 4" strokeWidth={2}
                    label={{ value: `MaxPain $${fmtPrice(mm.max_pain)}`, position: 'insideBottomRight', fill: '#ff9800', fontSize: 11 }} />
                )}
                {mm.gamma_flip && (
                  <ReferenceLine x={mm.gamma_flip} stroke="#9c27b0" strokeDasharray="12 6" strokeWidth={2}
                    label={{ value: `Flip $${fmtPrice(mm.gamma_flip)}`, position: 'insideTopLeft', fill: '#9c27b0', fontSize: 11 }} />
                )}
                <ReferenceLine y={0} stroke={theme.palette.divider} />
              </ComposedChart>
            </ResponsiveContainer>
            <Box sx={{ maxWidth: 400, mx: 'auto', mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Strike Range: &plusmn;{oiRangePct}% of spot
              </Typography>
              <Slider
                value={oiRangePct}
                onChange={(_, v) => setOiRangePct(v as number)}
                min={5} max={30} step={1}
                marks={[
                  { value: 5, label: '±5%' }, { value: 10, label: '±10%' },
                  { value: 15, label: '±15%' }, { value: 20, label: '±20%' },
                  { value: 25, label: '±25%' }, { value: 30, label: '±30%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `±${v}%`}
              />
            </Box>
          </Box>
        )}

        {/* Top 10 OI Movers */}
        {topMovers.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Top OI Movers</Typography>
            <ResponsiveContainer width="100%" height={Math.max(200, topMovers.length * 32)}>
              <BarChart
                data={topMovers.map((m) => ({
                  label: `$${m.strike} ${m.option_type === 'C' ? 'Call' : 'Put'}`,
                  oi_change: m.oi_change,
                  option_type: m.option_type,
                }))}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" tickFormatter={(v: number) => v.toLocaleString()} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={80} />
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString(), 'OI Change']}
                  contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                />
                <Bar dataKey="oi_change" name="OI Change">
                  {topMovers.map((m, i) => (
                    <Cell key={i} fill={m.oi_change >= 0 ? '#4caf50' : '#f44336'} opacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </Paper>

      {/* Section 2: Retail Momentum */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Retail Momentum</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>{retail.summary}</Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {retail.pc_volume_ratio != null && (
            <Paper sx={{ px: 2, py: 1 }} variant="outlined">
              <Typography variant="caption" color="text.secondary">P/C Vol Ratio</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{retail.pc_volume_ratio.toFixed(2)}</Typography>
            </Paper>
          )}
          {retail.price_change_pct != null && (
            <Paper sx={{ px: 2, py: 1 }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Price Change</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: retail.price_change_pct >= 0 ? '#4caf50' : '#f44336' }}>
                {retail.price_change_pct >= 0 ? '+' : ''}{retail.price_change_pct.toFixed(1)}%
              </Typography>
            </Paper>
          )}
          <Paper sx={{ px: 2, py: 1 }} variant="outlined">
            <Typography variant="caption" color="text.secondary">Sentiment</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Chip label={retail.retail_sentiment} size="small" color={chipColor(retail.retail_sentiment)} />
            </Typography>
          </Paper>
        </Box>

        {/* High Volume Strikes chart */}
        {volChartData.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>High Volume Strikes (Hot Spots)</Typography>
            <ResponsiveContainer width="100%" height={Math.max(200, volChartData.length * 32)}>
              <BarChart
                data={volChartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={100} />
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString(), 'Volume']}
                  contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                />
                <Bar dataKey="volume" name="Volume">
                  {volChartData.map((entry, i) => (
                    <Cell key={i} fill={entry.option_type === 'C' ? '#4caf50' : '#f44336'} opacity={0.85} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </Paper>

      {/* Max Pain History Chart */}
      <MaxPainHistoryChart ticker={data.ticker} />

      {/* Full GEX Profile — reusable component with slider + expiry picker */}
      <GexChart ticker={data.ticker} spotPriceOverride={data.spot_price} height={400} showStats={false} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Max Pain History Chart
// ---------------------------------------------------------------------------

const RANGE_PRESETS = [
  { label: '1W', days: 7 },
  { label: '2W', days: 14 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
];

const MaxPainHistoryChart: React.FC<{ ticker: string }> = ({ ticker }) => {
  const theme = useTheme();
  const [days, setDays] = useState(7);
  const [data, setData] = useState<MaxPainHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    marketPulseService.getMaxPainHistory(ticker, days)
      .then((r) => setData(r.items))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [ticker, days]);

  if (loading) return <Paper sx={{ p: 2, mb: 3, textAlign: 'center' }}><CircularProgress size={24} /></Paper>;
  if (data.length === 0) return null;

  const formatDate = (d: string) => {
    const dt = new Date(d + 'T12:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Max Pain History</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {RANGE_PRESETS.map((p) => (
            <Button
              key={p.days}
              size="small"
              variant={days === p.days ? 'contained' : 'outlined'}
              onClick={() => setDays(p.days)}
              sx={{ minWidth: 40 }}
            >
              {p.label}
            </Button>
          ))}
        </Box>
      </Box>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11 }} />
          <YAxis
            domain={['auto', 'auto']}
            tickFormatter={(v: number) => `$${v}`}
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            labelFormatter={(label: string) => formatDate(label)}
            formatter={(value: number, name: string) => [`$${value.toFixed(2)}`, name === 'max_pain' ? 'Max Pain' : 'Spot Price']}
            contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
          />
          <Legend formatter={(value: string) => value === 'max_pain' ? 'Max Pain' : 'Spot Price'} />
          <Line type="monotone" dataKey="max_pain" stroke="#ff9800" strokeWidth={2} dot={{ r: 3 }} name="max_pain" />
          <Line type="monotone" dataKey="spot_price" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 3 }} name="spot_price" />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MarketPulse: React.FC = () => {
  const navigate = useNavigate();
  const { ticker: urlTicker } = useParams<{ ticker?: string }>();

  const [summaryData, setSummaryData] = useState<TickerPulseSummary[]>([]);
  const [detailData, setDetailData] = useState<TickerPulseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await marketPulseService.getSummary();
      setSummaryData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load market pulse');
    } finally {
      setLoading(false);
    }
  }, []);

  const [refreshing, setRefreshing] = useState(false);

  const fetchDetail = useCallback(async (ticker: string) => {
    setLoading(true);
    setError(null);
    try {
      let result = await marketPulseService.getDetail(ticker);
      if ('error' in result) {
        setError(String((result as { error: string }).error));
        setDetailData(null);
        return;
      }

      // Auto-refresh if data is older than 5 minutes
      if (result.computed_at) {
        const ageMs = Date.now() - new Date(result.computed_at).getTime();
        if (ageMs > 5 * 60 * 1000) {
          setDetailData(result); // Show stale data first
          setLoading(false);
          setRefreshing(true);
          try {
            await marketPulseService.refreshTicker(ticker);
            result = await marketPulseService.getDetail(ticker);
            if (!('error' in result)) {
              setDetailData(result);
            }
          } catch {
            // Refresh failed — keep showing stale data
          } finally {
            setRefreshing(false);
          }
          return;
        }
      }

      setDetailData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load pulse detail');
      setDetailData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (urlTicker) {
      fetchDetail(urlTicker.toUpperCase());
    } else {
      fetchSummary();
    }
  }, [urlTicker, fetchSummary, fetchDetail]);

  // Live price polling during market hours
  const pollTickers = useMemo(() => {
    if (urlTicker) return [urlTicker.toUpperCase()];
    return summaryData.map((r) => r.ticker);
  }, [urlTicker, summaryData]);

  useMarketPolling(pollTickers, useCallback((prices: Record<string, number>) => {
    if (urlTicker) {
      const live = prices[urlTicker.toUpperCase()];
      if (live != null) {
        setDetailData((prev) => prev ? { ...prev, spot_price: live } : prev);
      }
    } else {
      setSummaryData((prev) =>
        prev.map((row) => {
          const live = prices[row.ticker];
          return live != null ? { ...row, spot_price: live } : row;
        }),
      );
    }
  }, [urlTicker]));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {!urlTicker && (
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          Market Pulse
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && !urlTicker && summaryData.length > 0 && (
        <>
          <TopPicksSection onSelect={(t) => navigate(`/pulse/${t}`)} />
          <SummaryView data={summaryData} onSelect={(t) => navigate(`/pulse/${t}`)} />
        </>
      )}

      {!loading && !error && !urlTicker && summaryData.length === 0 && (
        <Alert severity="info">
          No pulse data available yet. The market pulse job runs at 9:20 AM ET on weekdays.
          You can trigger it manually from Admin &rarr; Manual Triggers.
        </Alert>
      )}

      {!loading && !error && urlTicker && detailData && (
        <DetailView data={detailData} onBack={() => navigate('/pulse')} refreshing={refreshing} />
      )}
    </Box>
  );
};

export default MarketPulse;
